import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

function getBase() {
  return (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
}

function getVariantOptions(v) {
  if (!v) return [];
  if (Array.isArray(v.options) && v.options.length) return v.options;
  const arr = [];
  if (v.option1 != null) arr.push(v.option1);
  if (v.option2 != null) arr.push(v.option2);
  if (v.option3 != null) arr.push(v.option3);
  return arr;
}

function matchVariant(variants, selectedOptions) {
  for (const v of variants) {
    if (!v || !v.available) continue;
    const opts = getVariantOptions(v);
    let ok = true;
    for (let i = 0; i < selectedOptions.length; i++) {
      if (opts[i] !== selectedOptions[i]) { ok = false; break; }
    }
    if (ok) return v;
  }
  return null;
}

function computeAvailabilityForSelects(variants, selects) {
  const selected = selects.map(s => s.value);

  selects.forEach((select, idx) => {
    Array.from(select.options).forEach(opt => {
      const val = opt.value;

      const exists = variants.some(v => {
        if (!v || !v.available) return false;
        const opts = getVariantOptions(v);

        if (opts[idx] !== val) return false;

        for (let j = 0; j < selected.length; j++) {
          if (j === idx) continue;
          if (selected[j] == null) continue;
          if (opts[j] !== selected[j]) return false;
        }
        return true;
      });

      opt.disabled = !exists;
    });

    const cur = select.selectedOptions && select.selectedOptions[0];
    if (cur && cur.disabled) {
      const firstOk = Array.from(select.options).find(o => !o.disabled);
      if (firstOk) select.value = firstOk.value;
    }
  });
}

function setSelectsToFirstAvailable(selects, variants) {
  const firstAvailable = variants.find(v => v && v.available);
  if (!firstAvailable) return;
  const opts = getVariantOptions(firstAvailable);
  selects.forEach((s, i) => {
    if (opts[i] != null) s.value = opts[i];
  });
}

function setSoldOutState(card, isSoldOut, message = '') {
  const addBtn = card.querySelector('[data-neuf-add]');
  const note = card.querySelector('[data-neuf-note]');
  if (!addBtn) return;

  if (isSoldOut) {
    card.classList.add('neuf-upcard--soldout');
    addBtn.disabled = true;
    addBtn.dataset.variantId = '';
    if (note) note.textContent = message || 'Niet op voorraad.';
  } else {
    card.classList.remove('neuf-upcard--soldout');
    if (note) note.textContent = '';
  }
}

function syncUpsellCard(card) {
  const variantsJsonEl = card.querySelector('[data-neuf-variants-json]');
  const addBtn = card.querySelector('[data-neuf-add]');
  const note = card.querySelector('[data-neuf-note]');
  if (!variantsJsonEl || !addBtn) return;

  const variants = JSON.parse(variantsJsonEl.textContent || '[]');
  const selects = Array.from(card.querySelectorAll('select[data-neuf-option-index]'));

  if (!card.dataset.neufDefaultsSet) {
    setSelectsToFirstAvailable(selects, variants);
    card.dataset.neufDefaultsSet = '1';
  }

  computeAvailabilityForSelects(variants, selects);

  const selectedOptions = selects.map(s => s.value);
  const v = matchVariant(variants, selectedOptions);

  if (v) {
    card.classList.remove('neuf-upcard--soldout');
    addBtn.dataset.variantId = String(v.id);
    addBtn.disabled = false;
    if (note) note.textContent = '';
  } else {
    addBtn.dataset.variantId = '';
    addBtn.disabled = true;
    card.classList.add('neuf-upcard--soldout');
    if (note) note.textContent = 'Niet op voorraad.';
  }
}

function initUpsells(root = document) {
  root.querySelectorAll('[data-neuf-upsell-card]').forEach(syncUpsellCard);
}

function formatTotalNlNoCurrency(cents) {
  try {
    const v = new Intl.NumberFormat('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
    return '€' + v;
  } catch (e) {
    const val = (cents / 100).toFixed(2).replace('.', ',');
    return '€' + val;
  }
}

async function refreshSubtotalText() {
  try {
    const cartRes = await fetch(getBase() + 'cart.js', { credentials: 'same-origin' });
    const cartData = await cartRes.json();
    const formatted = formatTotalNlNoCurrency(cartData.total_price);

    document.querySelectorAll('[data-cart-subtotal]').forEach(el => {
      el.setAttribute('value', formatted);
      el.textContent = formatted;
    });
  } catch (e) {}
}

async function addVariantToCart(variantId) {
  const res = await fetch(getBase() + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ id: Number(variantId), quantity: 1 })
  });

  if (!res.ok) {
    let msg = 'Niet op voorraad.';
    try {
      const data = await res.json();
      if (data && (data.description || data.message)) {
        msg = data.description || data.message;
      }
    } catch (e) {}
    throw new Error(msg);
  }

  try {
    return await res.json();
  } catch (e) {
    return {};
  }
}

function wireUpsellEventsOnce() {
  if (document.documentElement.dataset.neufUpsellWired === '1') return;
  document.documentElement.dataset.neufUpsellWired = '1';

  document.addEventListener('change', (e) => {
    const sel = e.target;
    if (!sel || !sel.matches('select[data-neuf-option-index]')) return;
    const card = sel.closest('[data-neuf-upsell-card]');
    if (!card) return;
    syncUpsellCard(card);
  });

  document.addEventListener('click', async (e) => {
    const btn = e.target && e.target.closest && e.target.closest('[data-neuf-add]');
    if (!btn) return;

    e.preventDefault();

    const card = btn.closest('[data-neuf-upsell-card]');
    if (!card) return;

    syncUpsellCard(card);

    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Toevoegen…';

    try {
      await addVariantToCart(variantId);

      btn.textContent = 'Toegevoegd';

      await refreshSubtotalText();

      document.dispatchEvent(
        new CustomEvent(ThemeEvents.cartUpdate, { detail: { data: {} } })
      );

      document.dispatchEvent(new CustomEvent(CartAddEvent.eventName));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:change'));

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
        syncUpsellCard(card);
      }, 900);
    } catch (err) {
      btn.textContent = originalText;

      // sold out or other add error: lock the card
      setSoldOutState(card, true, (err && err.message) ? err.message : 'Niet op voorraad.');
    }
  });

  /* Re init after drawer morph / refresh */
  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('[data-neuf-upsell]') || node.querySelector?.('[data-neuf-upsell]')) {
          initUpsells(node);
        }
      }
    }
  });

  obs.observe(document.documentElement, { childList: true, subtree: true });

  initUpsells(document);
}

wireUpsellEventsOnce();

class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    initUpsells(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      initUpsells(this);
    }
  };

  open() {
    this.showDialog();
    initUpsells(this);

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}